import { Marked } from 'marked';
import { SECTIONS, articleForPath, parseArticle, searchArticles, visibleTo, type HelpArticle } from './parse';

export { SECTIONS, visibleTo, type HelpArticle };

const files = import.meta.glob('./articles/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

/** Every help article, in section order. */
export const ARTICLES: HelpArticle[] = Object.entries(files)
  .map(([file, src]) => parseArticle(src, file))
  .sort((a, b) => SECTIONS.indexOf(a.section) - SECTIONS.indexOf(b.section) || a.order - b.order || a.title.localeCompare(b.title));

export const articleById = (id: string) => ARTICLES.find((a) => a.id === id);
export const articleFor = (path: string) => articleForPath(ARTICLES, path);
export const search = (q: string) => searchArticles(ARTICLES, q);

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** Where a link in an article may go: another article, a page of the help center, a web page, an email. */
function safeHref(href: string): string | null {
  if (href.startsWith('help:')) return `/help/${encodeURIComponent(href.slice(5))}`;
  if (/^(https?:|mailto:)/i.test(href) || href.startsWith('/') || href.startsWith('#')) return href;
  return null;
}

const md = new Marked({
  gfm: true,
  renderer: {
    // Articles are ours, but never trusted with raw HTML.
    html({ text }) {
      return escapeHtml(text);
    },
    image({ href, text }) {
      const file = href.replace(/^\/?help-images\//, '');
      if (!/^[a-z0-9-]+\.(jpg|png)$/i.test(file)) return escapeHtml(text);
      return `<figure class="help-figure"><img src="/help-images/${file}" alt="${escapeHtml(text)}" loading="lazy" decoding="async"><figcaption>${escapeHtml(text)}</figcaption></figure>`;
    },
    link({ href, tokens }) {
      const text = this.parser.parseInline(tokens);
      const to = safeHref(href);
      if (!to) return text;
      const outside = /^https?:/i.test(to);
      return `<a href="${escapeHtml(to)}"${outside ? ' target="_blank" rel="noopener noreferrer"' : ''}${to.startsWith('/help/') ? ' data-help-link' : ''}>${text}</a>`;
    }
  }
});

/** Any of our own Markdown (the staff changelog) as HTML, with the same safe rules as articles. */
export function renderMarkdown(text: string): string {
  return md.parse(text, { async: false }) as string;
}

/** An article's body as HTML. */
export function renderArticle(a: HelpArticle): string {
  return md.parse(a.body, { async: false }) as string;
}
